import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

class AdminSettings extends React.Component {
  state = {
    settings: null,
    stats: null,
    loading: true,
    saving: false,
    triggeringReminders: false,
    reminders: [],
    remindersPage: 1,
    remindersPages: 1,
    showHistory: false,
    form: {
      subscription_price: '',
      subscription_duration_days: '',
      free_message_limit: '',
      chat_message_limit: '',
      chat_timeout_minutes: '',
      expiry_reminder_enabled: true,
      reminder_7_day: true,
      reminder_3_day: true,
      reminder_1_day: true,
      offer_enabled: false,
      offer_price: '',
      offer_scope: 'global',
      offer_start_at: '',
      offer_end_at: '',
      brand_name: '',
      brand_logo_url: '',
      support_email: '',
      support_phone: '',
      twilio_account_sid: '',
      twilio_auth_token: '',
      twilio_phone_number: '',
      whatsapp_enabled: false,
      whatsapp_from_number: ''
    }
  };

  componentDidMount() { this.load(); }

  formatDateTimeLocal = (value) => {
    if (!value) return '';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toISOString().slice(0, 16);
  };

  load = async () => {
    this.setState({ loading: true });
    try {
      const t = localStorage.getItem('adminToken');
      const r = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${t}` } });
      const s = r.data.settings;
      this.setState({
        settings: s,
        stats: r.data.stats,
        form: {
          subscription_price: (s.subscription_price_paise / 100).toString(),
          subscription_duration_days: s.subscription_duration_days.toString(),
          free_message_limit: s.free_message_limit.toString(),
          chat_message_limit: s.chat_message_limit?.toString() || '5',
          chat_timeout_minutes: s.chat_timeout_minutes?.toString() || '30',
          expiry_reminder_enabled: s.expiry_reminder_enabled,
          reminder_7_day: (s.expiry_reminder_days || []).includes(7),
          reminder_3_day: (s.expiry_reminder_days || []).includes(3),
          reminder_1_day: (s.expiry_reminder_days || []).includes(1),
          offer_enabled: s.offer_enabled || false,
          offer_price: s.offer_price_paise ? (s.offer_price_paise / 100).toString() : '',
          offer_scope: s.offer_scope || 'global',
          offer_start_at: this.formatDateTimeLocal(s.offer_start_at),
          offer_end_at: this.formatDateTimeLocal(s.offer_end_at),
          brand_name: s.brand_name || 'sideo',
          brand_logo_url: s.brand_logo_url || '',
          support_email: s.support_email || '',
          support_phone: s.support_phone || '',
          twilio_account_sid: s.twilio_account_sid || '',
          twilio_auth_token: s.twilio_auth_token || '',
          twilio_phone_number: s.twilio_phone_number || '',
          whatsapp_enabled: s.whatsapp_enabled || false,
          whatsapp_from_number: s.whatsapp_from_number || ''
        },
        loading: false
      });
    } catch { toast.error('Failed to load settings'); this.setState({ loading: false }); }
  };

  loadHistory = async () => {
    try {
      const t = localStorage.getItem('adminToken');
      const r = await axios.get(`${API}/admin/settings/reminder-history?page=${this.state.remindersPage}`, { headers: { Authorization: `Bearer ${t}` } });
      this.setState({ reminders: r.data.reminders, remindersPages: r.data.pages });
    } catch { toast.error('Failed to load history'); }
  };

  save = async () => {
    this.setState({ saving: true });
    try {
      const t = localStorage.getItem('adminToken');
      const { form } = this.state;

      const basePrice = parseFloat(form.subscription_price);
      const offerPrice = form.offer_price ? parseFloat(form.offer_price) : null;
      if (form.offer_enabled) {
        if (!offerPrice || Number.isNaN(offerPrice) || offerPrice <= 0) {
          toast.error('Offer price is required when offer is enabled');
          this.setState({ saving: false });
          return;
        }
        if (!Number.isNaN(basePrice) && offerPrice >= basePrice) {
          toast.error('Offer price must be lower than base price');
          this.setState({ saving: false });
          return;
        }
        if (form.offer_start_at && form.offer_end_at) {
          const start = new Date(form.offer_start_at);
          const end = new Date(form.offer_end_at);
          if (end <= start) {
            toast.error('Offer end date must be after start date');
            this.setState({ saving: false });
            return;
          }
        }
      }
      
      const reminderDays = [];
      if (form.reminder_7_day) reminderDays.push(7);
      if (form.reminder_3_day) reminderDays.push(3);
      if (form.reminder_1_day) reminderDays.push(1);
      
      await axios.put(`${API}/admin/settings`, {
        subscription_price_paise: Math.round(parseFloat(form.subscription_price) * 100),
        subscription_duration_days: parseInt(form.subscription_duration_days),
        free_message_limit: parseInt(form.free_message_limit),
        chat_message_limit: parseInt(form.chat_message_limit),
        chat_timeout_minutes: parseInt(form.chat_timeout_minutes),
        expiry_reminder_enabled: form.expiry_reminder_enabled,
        expiry_reminder_days: reminderDays,
        offer_enabled: form.offer_enabled,
        offer_price_paise: form.offer_price ? Math.round(parseFloat(form.offer_price) * 100) : null,
        offer_scope: form.offer_scope,
        offer_start_at: form.offer_start_at || null,
        offer_end_at: form.offer_end_at || null,
        brand_name: form.brand_name,
        brand_logo_url: form.brand_logo_url,
        support_email: form.support_email,
        support_phone: form.support_phone,
        twilio_account_sid: form.twilio_account_sid,
        twilio_auth_token: form.twilio_auth_token,
        twilio_phone_number: form.twilio_phone_number,
        whatsapp_enabled: form.whatsapp_enabled,
        whatsapp_from_number: form.whatsapp_from_number
      }, { headers: { Authorization: `Bearer ${t}` } });
      
      toast.success('Settings saved');
      this.load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    this.setState({ saving: false });
  };

  triggerReminders = async () => {
    this.setState({ triggeringReminders: true });
    try {
      const t = localStorage.getItem('adminToken');
      const r = await axios.post(`${API}/admin/settings/send-reminders-now`, {}, { headers: { Authorization: `Bearer ${t}` } });
      toast.success(`Sent ${r.data.sent} reminders`);
      this.load();
    } catch { toast.error('Failed to send reminders'); }
    this.setState({ triggeringReminders: false });
  };

  render() {
    const { settings, stats, loading, saving, triggeringReminders, form, showHistory, reminders, remindersPage, remindersPages } = this.state;

    if (loading) return <AdminLayout><div className="text-center py-8 text-slate-400">Loading...</div></AdminLayout>;

    return (
      <AdminLayout>
        <div className="space-y-6 max-w-4xl">
          <div><h1 className="text-2xl font-bold text-white">Platform Settings</h1><p className="text-slate-400">Configure pricing and reminders</p></div>

          {/* Pricing Settings */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">💰 Pricing Configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Subscription Price (₹)</label>
                <input type="number" value={form.subscription_price} onChange={(e) => this.setState({ form: { ...form, subscription_price: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" min="1" step="1" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Duration (days)</label>
                <input type="number" value={form.subscription_duration_days} onChange={(e) => this.setState({ form: { ...form, subscription_duration_days: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" min="1" />
              </div>
            </div>
            <p className="text-slate-500 text-sm mt-3">Current: ₹{settings?.subscription_price_paise / 100} for {settings?.subscription_duration_days} days</p>
          </div>

          {/* Chat & Message Limits */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">💬 Chat & Message Limits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Chat Message Limit</label>
                <input type="number" value={form.chat_message_limit} onChange={(e) => this.setState({ form: { ...form, chat_message_limit: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" min="1" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Chat Timeout (minutes)</label>
                <input type="number" value={form.chat_timeout_minutes} onChange={(e) => this.setState({ form: { ...form, chat_timeout_minutes: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" min="1" />
              </div>
            </div>
            <p className="text-slate-500 text-sm mt-3">These limits apply to new chat sessions and message caps.</p>
          </div>

          {/* Offers */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">🏷️ Promotional Offer</h2>
                <p className="text-slate-400 text-sm">Override pricing for campaigns</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.offer_enabled} onChange={(e) => this.setState({ form: { ...form, offer_enabled: e.target.checked } })} className="w-5 h-5 rounded" />
                <span className="text-white">Enabled</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Offer Price (₹)</label>
                <input type="number" value={form.offer_price} onChange={(e) => this.setState({ form: { ...form, offer_price: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" min="1" step="1" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Offer Scope</label>
                <select value={form.offer_scope} onChange={(e) => this.setState({ form: { ...form, offer_scope: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white">
                  <option value="global">Global (all users)</option>
                  <option value="new_users">New users only</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Offer Start</label>
                <input type="datetime-local" value={form.offer_start_at} onChange={(e) => this.setState({ form: { ...form, offer_start_at: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Offer End</label>
                <input type="datetime-local" value={form.offer_end_at} onChange={(e) => this.setState({ form: { ...form, offer_end_at: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
            </div>
          </div>

          {/* Expiry Reminders */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">📧 Expiry Reminders</h2>
                <p className="text-slate-400 text-sm">Auto-send SMS reminders before subscription expires</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.expiry_reminder_enabled} onChange={(e) => this.setState({ form: { ...form, expiry_reminder_enabled: e.target.checked } })} className="w-5 h-5 rounded" />
                <span className="text-white">Enabled</span>
              </label>
            </div>
            
            <div className="flex flex-wrap gap-4 mb-4">
              <label className="flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-lg cursor-pointer">
                <input type="checkbox" checked={form.reminder_7_day} onChange={(e) => this.setState({ form: { ...form, reminder_7_day: e.target.checked } })} className="w-4 h-4" />
                <span className="text-white">7 days before</span>
              </label>
              <label className="flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-lg cursor-pointer">
                <input type="checkbox" checked={form.reminder_3_day} onChange={(e) => this.setState({ form: { ...form, reminder_3_day: e.target.checked } })} className="w-4 h-4" />
                <span className="text-white">3 days before</span>
              </label>
              <label className="flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-lg cursor-pointer">
                <input type="checkbox" checked={form.reminder_1_day} onChange={(e) => this.setState({ form: { ...form, reminder_1_day: e.target.checked } })} className="w-4 h-4" />
                <span className="text-white">1 day before</span>
              </label>
            </div>

            <div className="flex gap-4">
              <button onClick={this.triggerReminders} disabled={triggeringReminders} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">
                {triggeringReminders ? 'Sending...' : '🚀 Send Reminders Now'}
              </button>
              <button onClick={() => { this.setState({ showHistory: !showHistory }); if (!showHistory) this.loadHistory(); }} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg">
                {showHistory ? 'Hide History' : '📜 View History'}
              </button>
            </div>

            {stats && <p className="text-slate-500 text-sm mt-3">Reminders sent today: {stats.reminders_sent_today}</p>}
          </div>

          {/* Reminder History */}
          {showHistory && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Reminder History</h3>
              {reminders.length === 0 ? <p className="text-slate-500">No reminders sent yet</p> : (
                <div className="space-y-2">
                  {reminders.map((r, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                      <div>
                        <span className={`px-2 py-1 rounded text-xs ${r.success ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{r.reminder_type}</span>
                        <span className="text-slate-400 text-sm ml-3">{r.owner_phone}</span>
                      </div>
                      <span className="text-slate-500 text-sm">{new Date(r.sent_at).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              )}
              {remindersPages > 1 && (
                <div className="flex justify-between mt-4">
                  <button onClick={() => this.setState({ remindersPage: remindersPage - 1 }, this.loadHistory)} disabled={remindersPage === 1} className="text-slate-400 disabled:opacity-50">← Prev</button>
                  <span className="text-slate-500">{remindersPage}/{remindersPages}</span>
                  <button onClick={() => this.setState({ remindersPage: remindersPage + 1 }, this.loadHistory)} disabled={remindersPage === remindersPages} className="text-slate-400 disabled:opacity-50">Next →</button>
                </div>
              )}
            </div>
          )}

          {/* Branding */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">🎨 Branding</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Brand Name</label>
                <input type="text" value={form.brand_name} onChange={(e) => this.setState({ form: { ...form, brand_name: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Logo URL</label>
                <input type="text" value={form.brand_logo_url} onChange={(e) => this.setState({ form: { ...form, brand_logo_url: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Support Email</label>
                <input type="email" value={form.support_email} onChange={(e) => this.setState({ form: { ...form, support_email: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Support Phone</label>
                <input type="text" value={form.support_phone} onChange={(e) => this.setState({ form: { ...form, support_phone: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
            </div>
          </div>

          {/* Twilio & WhatsApp */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">📲 Twilio & WhatsApp</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Twilio Account SID</label>
                <input type="text" value={form.twilio_account_sid} onChange={(e) => this.setState({ form: { ...form, twilio_account_sid: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Twilio Auth Token</label>
                <input type="password" value={form.twilio_auth_token} onChange={(e) => this.setState({ form: { ...form, twilio_auth_token: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">Twilio Phone Number</label>
                <input type="text" value={form.twilio_phone_number} onChange={(e) => this.setState({ form: { ...form, twilio_phone_number: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">WhatsApp From Number</label>
                <input type="text" value={form.whatsapp_from_number} onChange={(e) => this.setState({ form: { ...form, whatsapp_from_number: e.target.value } })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white" />
              </div>
            </div>
            <div className="mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.whatsapp_enabled} onChange={(e) => this.setState({ form: { ...form, whatsapp_enabled: e.target.checked } })} className="w-5 h-5 rounded" />
                <span className="text-white">Enable WhatsApp notifications</span>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <button onClick={this.save} disabled={saving} className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-xl disabled:opacity-50">
            {saving ? 'Saving...' : '💾 Save Settings'}
          </button>

          {/* Last Updated */}
          {settings?.updated_at && (
            <p className="text-center text-slate-600 text-sm">
              Last updated: {new Date(settings.updated_at).toLocaleString('en-IN')} by {settings.updated_by || 'system'}
            </p>
          )}
        </div>
      </AdminLayout>
    );
  }
}

export default AdminSettings;
